import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
// https://www.gatsbyjs.com/docs/add-seo-component/

const SEGMENT_LABELS = {
  archive: 'Archive',
  built: 'Built',
  certifications: 'Certifications',
  games: 'Games',
  pensieve: 'Pensieve',
  tags: 'Tags',
};

const formatSegmentLabel = segment =>
  decodeURIComponent(segment)
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const buildBreadcrumbSchema = (siteUrl, pathname) => {
  const cleanPathname = pathname.split(/[?#]/)[0];
  const segments = cleanPathname.split('/').filter(Boolean);

  if (segments.length === 0 || cleanPathname.startsWith('/404')) {
    return null;
  }

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${siteUrl}/`,
    },
  ];

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: SEGMENT_LABELS[segment] || formatSegmentLabel(segment),
      item: `${siteUrl}${currentPath}/`,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
};

const Head = ({ title, description, image }) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            siteName
            defaultDescription: description
            siteUrl
            defaultImage: image
            twitterUsername
            keywords
          }
        }
      }
    `,
  );

  const {
    defaultTitle,
    siteName,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
    keywords,
  } = site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image || defaultImage}`,
    url: `${siteUrl}${pathname}`,
  };

  const breadcrumbSchema = buildBreadcrumbSchema(siteUrl, pathname);
  const websiteName = siteName || defaultTitle;
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: siteUrl,
    name: websiteName,
    alternateName: 'oyerohabib.com',
  };
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Oyero Habibulah',
    url: siteUrl,
  };

  return (
    <Helmet title={title} defaultTitle={seo.title} titleTemplate={`%s — ${defaultTitle}`}>
      <html lang="en" />

      <link rel="icon" href="/favicon.png" />
      <link rel="canonical" href={seo.url} />
      <link rel="sitemap" type="application/xml" href={`${siteUrl}/sitemap.xml`} />

      <meta name="description" content={seo.description} />
      <meta name="author" content={websiteName} />
      <meta name="application-name" content={websiteName} />
      <meta name="image" content={seo.image} />
      {keywords && <meta name="keywords" content={keywords} />}

      <meta property="og:title" content={seo.title} />
      <meta property="og:site_name" content={websiteName} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      <meta name="google-site-verification" content="h5x7MUa1Ska7VprnmQFOtySOHSMgPBsbt8CRPDmdlE4" />

      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
};

Head.defaultProps = {
  title: null,
  description: null,
  image: null,
};
